'use client'

import {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { noop } from 'lodash'

import { UiState, useLatestCallback } from '@marketplace-web/shared/ui-state-util'

import { ClientInitialItemUploadDataContextType } from '../types/context'
import { ConfigurationType } from '../types'
import { getIsPreviousLister, getIsSecondDayLister, getItemUploadConfiguration } from '../api'
import { transformItemUploadConfigDto } from '../transformers/item-upload-configuration'

import { useUiStateContext } from './UiStateContext'
import useFetchItem from '../hooks/useFetchItem'

const initialConfiguration: ConfigurationType = {
  photoTip: null,
  catalogFieldsOrder: [],
  maxShippingPrice: null,
  abTests: {},
  firstTimeListingGuideline: null,
  uploadSessionId: '',
  priceRange: {
    minimum: { amount: '', currencyCode: '' },
    maximum: { amount: '', currencyCode: '' },
  },
  currency: '',
  isFirstTimeLister: null,
}

export const ClientInitialItemUploadDataContext =
  createContext<ClientInitialItemUploadDataContextType>({
    configuration: initialConfiguration,
    setConfiguration: noop,
    isPreviousLister: false,
    isSecondDayLister: false,
    isABTestEnabled: () => false,
    getABTest: () => undefined,
    updateCurrency: noop,
  })

export const ClientInitialItemUploadDataContextProvider = ({
  id,
  children,
}: {
  id: number | null
  children: ReactNode
}) => {
  const { setUiState } = useUiStateContext()

  const [configuration, setConfiguration] = useState<ConfigurationType>(initialConfiguration)
  const [isSecondDayLister, setIsSecondDayLister] = useState(false)
  const [isPreviousLister, setIsPreviousLister] = useState(false)

  const updateCurrency = useLatestCallback((value: string) => {
    if (configuration.currency === value) return

    setConfiguration(prevConfig => ({
      ...prevConfig,
      currency: value,
    }))
  })

  const { fetchItem } = useFetchItem({ onCurrencyUpdate: updateCurrency })

  const loadInitialItemUploadData = useCallback(async () => {
    const [configResp, previousListerResp, secondDayListerResp] = await Promise.all([
      getItemUploadConfiguration(),
      getIsPreviousLister(),
      getIsSecondDayLister(),
    ])

    if (
      'errors' in configResp ||
      'errors' in previousListerResp ||
      'errors' in secondDayListerResp
    ) {
      setUiState(UiState.Failure)

      return
    }

    const configurationModel = transformItemUploadConfigDto(configResp)

    setConfiguration(configurationModel)
    setIsPreviousLister(previousListerResp.value)
    setIsSecondDayLister(secondDayListerResp.value)

    fetchItem({ id, tempUuid: configurationModel.uploadSessionId })
  }, [id, setConfiguration, setIsPreviousLister, setIsSecondDayLister, setUiState, fetchItem])

  const isABTestEnabled = useCallback(
    (testName: string) => {
      const test = configuration.abTests[testName]

      if (!test) return false

      return test.variant?.toLowerCase() === 'on'
    },
    [configuration],
  )

  const getABTest = useCallback(
    (testName: string) => configuration.abTests[testName],
    [configuration],
  )

  useEffect(() => {
    loadInitialItemUploadData()
  }, [loadInitialItemUploadData])

  const providerValue = useMemo(
    () => ({
      isSecondDayLister,
      isPreviousLister,
      configuration,
      setConfiguration,
      isABTestEnabled,
      getABTest,
      updateCurrency,
    }),
    [
      configuration,
      isPreviousLister,
      isSecondDayLister,
      isABTestEnabled,
      getABTest,
      updateCurrency,
    ],
  )

  return (
    <ClientInitialItemUploadDataContext.Provider value={providerValue}>
      {children}
    </ClientInitialItemUploadDataContext.Provider>
  )
}

export const useClientInitialItemUploadDataContext = () => {
  const context = useContext(ClientInitialItemUploadDataContext)

  if (!context) {
    throw new Error(
      'useClientInitialItemUploadDataContext must be used within a ClientInitialItemUploadDataContextProvider',
    )
  }

  return context
}
